// TODO: clean this file up! just getting this in for demo purposes

import {
  Box,
  chakra,
  FormatNumber,
  Image,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { simulateContract, watchContractEvent } from "@wagmi/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { motion, useAnimation } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Address } from "viem";
import { useConfig, useWriteContract } from "wagmi";

import { dailySpinToaster, REWARD_CATEGORY_METADATA } from "./constants";
import { CostPill } from "./CostPill";
import { generateFakeDataSpin } from "./FakeSpinHelper";
import { FloatingRewardsBackground } from "./FloatingRewardsBackground";
import { SpinButton } from "./SpinButton";
import chestIcon from "/assets/daily-spin/treasure-chest.svg";
import wheelSvg from "/assets/daily-spin/wheel-of-fortune.svg";
import polygonSvg from "/assets/daily-spin/wheel-ticker.svg";

import { Confetti } from "@/components/confetti";
import { RoundedBadge } from "@/components/rounded-badge";
import { toaster } from "@/components/ui/toaster";
import { dailySpinChainId } from "@/constants/shared";
import { useAuth } from "@/hooks/useAuth";
import { useDailySpinOffchain } from "@/hooks/useDailySpinOffchain";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";
import { DAILY_SPIN_ABI } from "@/network/abi/daily-spin";
import { RewardCategory, RewardRecord } from "@/types";
import { wrapMetamaskError } from "@/utils";

dayjs.extend(utc);

export type SpinTickerItem = {
  walletAddress: string;
  rewardCategory: string;
  rewardAmount: number;
  timestamp: number;
  id: string; // Unique ID for React keys
};

// Define wheel segments in exact order, starting from top
const WHEEL_SEGMENTS = [
  REWARD_CATEGORY_METADATA[RewardCategory.Jackpot], // Pink - Jackpot
  REWARD_CATEGORY_METADATA[RewardCategory.PP], // Green - XP
  REWARD_CATEGORY_METADATA[RewardCategory.RaffleTicket], // Purple - Raffle Tickets
  REWARD_CATEGORY_METADATA[RewardCategory.PP], // Green - XP
  REWARD_CATEGORY_METADATA[RewardCategory.PlumeToken], // Red - Plume
  REWARD_CATEGORY_METADATA[RewardCategory.RaffleTicket], // Purple - Raffle Tickets
  REWARD_CATEGORY_METADATA[RewardCategory.PP], // Green - XP
  REWARD_CATEGORY_METADATA[RewardCategory.Nothing], // Gray - Nothing
  REWARD_CATEGORY_METADATA[RewardCategory.RaffleTicket], // Purple - Raffle Tickets
  REWARD_CATEGORY_METADATA[RewardCategory.PP], // Green - XP
  REWARD_CATEGORY_METADATA[RewardCategory.PlumeToken], // Red - Plume
  REWARD_CATEGORY_METADATA[RewardCategory.PP], // Green - XP
];

const SEGMENT_ANGLE = 360 / WHEEL_SEGMENTS.length; // 30 degrees per segment

const getRandomSegmentIndexForCategory = (category: RewardCategory): number => {
  // Get the reward object for the given category.
  const targetReward = REWARD_CATEGORY_METADATA[category];

  // Find all indices in WHEEL_SEGMENTS that match the target reward.
  const matchingIndices = WHEEL_SEGMENTS.reduce<number[]>(
    (indices, segment, index) => {
      if (segment === targetReward) {
        indices.push(index);
      }
      return indices;
    },
    [],
  );

  if (matchingIndices.length === 0) {
    throw new Error(`No wheel segment found for category: ${category}`);
  }

  // Pick a random index from the matchingIndices array.
  const randomIndex =
    matchingIndices[Math.floor(Math.random() * matchingIndices.length)];
  return randomIndex;
};

const getRotationFromCategory = (rewardCategory: RewardCategory) => {
  const targetSegmentIndex = getRandomSegmentIndexForCategory(rewardCategory);
  const targetAngle = -(targetSegmentIndex * SEGMENT_ANGLE);
  const rotation = FULL_ROTATION + targetAngle;

  return { reward: WHEEL_SEGMENTS[targetSegmentIndex], rotation };
};
const baseUrl = window.location.origin + "/assets/daily-spin/";

const FULL_ROTATION = -360 * 3;

// This is a utility function to enable dynamic typeguards for enum casting
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isRewardCategory = (value: any): value is RewardCategory => {
  return Object.values(RewardCategory).includes(value);
};

const MotionBox = chakra(motion.div);

export const FortuneWheel = ({
  mostRecentReward,
  recentRewardIsLoading,
  setLocalRecentSpin,
}: {
  mostRecentReward: RewardRecord | undefined;
  recentRewardIsLoading: boolean;
  setLocalRecentSpin: (localRecentSpin: RewardRecord) => void;
}) => {
  const wagmiConfig = useConfig();

  const { totalSpinPlumeRewards } = useDailySpinOffchain();
  const { refetchDailySpinStats, hasSpunToday, dailySpinStreak, spinPrice } =
    useDailySpinOnchain();
  const { address, isAuthenticated } = useAuth();
  // TODO: reconsider variable names
  // Currently, spinning refers to the UI state of whether or not the wheel is still in motion
  // isLoadingPrize refers to whether or not we've retrieved a prize from the backend
  // Although both are accurately named, one refers to the UI and one refers to our data state
  // There may be an opportunity to unify the context in which the names are considered
  const [isSpinning, setIsSpinning] = useState(false);
  const [isLoadingPrize, setIsLoadingPrize] = useState(false);

  // For the realtime spin notifications
  const [recentSpins, setRecentSpins] = useState<SpinTickerItem[]>(() => [
    generateFakeDataSpin(),
  ]);

  const [currentRewardCategory, setCurrentRewardCategory] = useState<
    RewardCategory | undefined
  >();
  const [currentRewardAmount, setCurrentRewardAmount] = useState<
    bigint | undefined
  >();
  const [showConfetti, setShowConfetti] = useState(false);

  const controls = useAnimation();
  const spinAudioRef = useRef(new Audio(baseUrl + "spin-sound.mp3"));
  const jackpotAudioRef = useRef(new Audio(baseUrl + "jackpot.mp3"));
  const winAudioRef = useRef(new Audio(baseUrl + "win.mp3"));
  const lostAudioRef = useRef(new Audio(baseUrl + "lost.mp3"));
  const containerRef = useRef<HTMLDivElement>(null);

  // Reference to store the unwatch function
  const unwatchRef = useRef<(() => void) | undefined>(undefined);

  // Make sure to clean up any active event listeners when component unmounts
  useEffect(() => {
    return () => {
      if (unwatchRef.current) {
        unwatchRef.current();
      }
    };
  }, []);

  // Timer for sending up fake spin data into the background
  useEffect(() => {
    // Function to add new fake spins periodically
    const addFakeSpin = () => {
      const fakeSpin = generateFakeDataSpin();
      setRecentSpins((prev) => [fakeSpin, ...prev].slice(0, 10));
    };

    // Set up interval for continuous fake spins (every 2-4 seconds)
    const interval = setInterval(
      () => {
        addFakeSpin();
      },
      3000 + Math.random() * 8000,
    );

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  // Set volume for all audio elements
  useEffect(() => {
    const audioElements = [
      spinAudioRef.current,
      jackpotAudioRef.current,
      winAudioRef.current,
      lostAudioRef.current,
    ];

    audioElements.forEach((audio) => {
      if (audio) {
        audio.volume = 0.08; // Adjust this value between 0.0 and 1.0 as needed
      }
    });
  }, []);

  const { writeContractAsync } = useWriteContract();

  useEffect(() => {
    const audio = spinAudioRef.current;
    if (!audio || !isLoadingPrize) return;

    // Ensure the audio doesn't loop the full track automatically.
    audio.loop = false;

    const handleTimeUpdate = () => {
      if (audio.currentTime >= 0.25 && isLoadingPrize) {
        audio.currentTime = 0.02;
        audio.play().catch(console.error);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [isLoadingPrize]);

  const handleSpin = async () => {
    try {
      if (isSpinning) {
        toaster.error({
          title: "You are already spinning",
          description: "Please wait for your previous spin to complete",
        });
        return;
      }

      /* Flow:
       * (1)Simulate first, if that passes then start the spin
       * (2)If simulation fails, check if it's because of low balance
       * (3)If it's not a low balance error, throw the error so it can be handled by the caller
       * (4)Then start the animation
       * (5)Then call MM (so its visibly doing something)
       * (6)Then on success start the audio
       * (7)Then watch the contract for the SpinCompleted event
       * (8)Once the event is detected, set the reward category and amount
       * (9)Then stop the animation and update the UI to show the reward
       */

      try {
        await simulateContract(wagmiConfig, {
          abi: DAILY_SPIN_ABI,
          chainId: dailySpinChainId,
          address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
          functionName: "startSpin",
          value: spinPrice,
          account: address as Address,
        });
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes("exceeds the balance of the account")
        ) {
          dailySpinToaster.create({
            type: "error",
            title: "You don’t have enough PLUME",
            description: "1 Spin = 2 PLUME, bridge or swap to spin",
          });
          return;
        }
        throw error;
      }

      setIsSpinning(true);

      await controls.stop();
      await controls.set({ rotate: 0 });
      controls.start({
        rotate: FULL_ROTATION,
        transition: {
          duration: 1,
          repeat: Infinity,
          ease: "linear",
        },
      });

      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalAttemptedDailySpin, {
        spin_streak: dailySpinStreak,
      });
      try {
        await writeContractAsync({
          abi: DAILY_SPIN_ABI,
          chainId: dailySpinChainId,
          address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
          functionName: "startSpin",
          value: spinPrice,
        });
      } catch (error) {
        const wrappedError = wrapMetamaskError(error);
        if (wrappedError.name === "4001") {
          console.warn("🛑 User rejected the wallet request.");
          return;
        }
        throw wrappedError;
      }

      // Play spin sound
      if (spinAudioRef.current) {
        spinAudioRef.current.currentTime = 0;
        spinAudioRef.current.play().catch(console.error);
      }

      setIsLoadingPrize(true);
      setCurrentRewardCategory(undefined);
      setShowConfetti(false);

      // Clean up any previous event listener if exists
      if (unwatchRef.current) {
        unwatchRef.current();
      }

      const unwatch = watchContractEvent(wagmiConfig, {
        address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
        abi: DAILY_SPIN_ABI,
        chainId: dailySpinChainId,
        eventName: "SpinCompleted",
        args: { walletAddress: address },
        pollingInterval: 100,
        onLogs: async (logs) => {
          const { rewardCategory, rewardAmount } = logs[0].args;
          if (!isRewardCategory(rewardCategory) || rewardAmount === undefined)
            return;
          const { rotation } = getRotationFromCategory(rewardCategory);
          setIsLoadingPrize(false);
          await controls.start({
            rotate: rotation,
            transition: {
              type: "spring",
              stiffness: 80,
              damping: 60,
              mass: 1.5,
            },
          });
          setCurrentRewardCategory(rewardCategory);
          setShowConfetti(rewardCategory === RewardCategory.Jackpot);
          setCurrentRewardAmount(rewardAmount);
          if (
            rewardCategory === RewardCategory.Jackpot &&
            jackpotAudioRef.current
          ) {
            jackpotAudioRef.current.currentTime = 0;
            jackpotAudioRef.current.play().catch(console.error);
          } else if (
            rewardCategory === RewardCategory.Nothing &&
            lostAudioRef.current
          ) {
            lostAudioRef.current.currentTime = 0;
            lostAudioRef.current.play().catch(console.error);
          } else if (winAudioRef.current) {
            winAudioRef.current.currentTime = 0;
            winAudioRef.current.play().catch(console.error);
          }
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCompletedDailySpin, {
            spin_streak: dailySpinStreak,
            reward_given: rewardCategory,
            // TODO: add logic to handle whether user has spun before
            first_time: false,
          });
          refetchDailySpinStats()
            .catch(() => {
              console.error("Error refetching daily spin stats");
            })
            .finally(() => {
              setIsSpinning(false);
            });

          const localRecentSpin: RewardRecord = {
            rewardCategory,
            rewardAmount: Number(rewardAmount),
            streak: 0, // this will get overridden by the history data on the DailySpinHistory component
            dateStr: dayjs().utc().format("YYYY-MM-DD"),
          };
          setLocalRecentSpin(localRecentSpin);
        },
      });

      // Store the unwatch function in the ref
      unwatchRef.current = unwatch;
    } catch (error) {
      console.error("Error spinning the wheel:", error);
      spinAudioRef.current?.pause();
      await controls.stop();
      await controls.set({ rotate: 0 });
      setIsSpinning(false);

      // Clean up the event listener in case of error
      if (unwatchRef.current) {
        unwatchRef.current();
        unwatchRef.current = undefined;
      }
    }
  };

  const currentRewardMetadata = useMemo(() => {
    // If user is not authenticated, show the default "reward awaits" state
    if (!isAuthenticated) {
      return undefined;
    }

    // if we dont have a "current" reward (from a just-now spin), then...
    if (!currentRewardCategory) {
      // if we did spin today, then once it has loaded show the last reward in the lower box
      if (hasSpunToday && !recentRewardIsLoading && mostRecentReward) {
        return REWARD_CATEGORY_METADATA[
          mostRecentReward.rewardCategory as RewardCategory
        ];
      }
      // otherwise, return undefined which is spinner until loaded and then shows "reward awaits you"
      return undefined;
    }
    return REWARD_CATEGORY_METADATA[currentRewardCategory];
  }, [
    currentRewardCategory,
    hasSpunToday,
    recentRewardIsLoading,
    mostRecentReward,
    isAuthenticated,
  ]);

  return (
    <Box
      ref={containerRef}
      w="full"
      h={["634px", "700px", "732px", "840px"]}
      bg="bg"
      position="relative"
      borderRadius="3xl"
      p={4}
      overflow="hidden"
      borderWidth="thin"
    >
      <Box
        position="absolute"
        top={["13px", "13px", "26px"]}
        right={["15px", "15px", "30px"]}
        zIndex={100}
        hideBelow="md"
      >
        <CostPill />
      </Box>

      {/* {isAuthenticated && (
        <Box position="absolute" top="10" right="10" zIndex={5}>
          <Button variant="outline" size="xs" onClick={handleSpin}>
            ADMIN SPIN
          </Button>
        </Box>
      )} */}
      <FloatingRewardsBackground recentSpins={recentSpins} />
      <Confetti
        showConfetti={showConfetti}
        setShowConfetti={setShowConfetti}
        containerRef={containerRef}
      />

      <Box
        position="relative"
        width={["308px", "320px", "360px", "456px"]}
        height={["308px", "320px", "360px", "456px"]}
        mx="auto"
        mt={["40px", "60px", "80px"]}
        zIndex={100}
        textAlign="center"
      >
        <Image
          src={polygonSvg}
          position="absolute"
          top="-12px"
          left="50%"
          transform="translateX(-50%)"
          width="24px"
          height="auto"
          zIndex={2}
        />
        <MotionBox
          animate={controls}
          zIndex={4}
          width="full"
          height="full"
          backgroundImage={`url(${wheelSvg})`}
          backgroundSize="contain"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          backgroundColor="#f9f9f9"
          borderRadius="full"
        ></MotionBox>
        <SpinButton handleSpin={handleSpin} isSpinning={isSpinning} />
        {totalSpinPlumeRewards && (
          <RoundedBadge
            bgColor="#FFF8E0"
            color="#963D01"
            h={[9, 9, 10]}
            mt={[6, 6, 4, 4]}
            px={3}
          >
            🔥 <FormatNumber value={totalSpinPlumeRewards} notation="compact" />{" "}
            PLUME Paid Out
          </RoundedBadge>
        )}
      </Box>

      <Box
        position="absolute"
        bottom="0"
        left="0"
        width="full"
        height={["160px", "190px", "200px", "216px"]}
        bg={
          currentRewardMetadata
            ? currentRewardMetadata.bgColor
            : REWARD_CATEGORY_METADATA[RewardCategory.NotYetSpun].bgColor
        }
        transition="background-color 0.3s ease"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={100}
      >
        <Box textAlign="center" width="full">
          {currentRewardMetadata ? (
            <>
              <Text fontSize={["xl", "xl", "2xl"]} fontWeight="bold" mb={4}>
                {currentRewardMetadata.title}
              </Text>
              <Box
                bg={currentRewardMetadata.pillColor}
                color={currentRewardMetadata.color}
                py={2}
                px={4}
                borderRadius="full"
                display="inline-flex"
                alignItems="center"
                mb={4}
              >
                <Image
                  src={currentRewardMetadata.icon}
                  alt={currentRewardMetadata.rewardCurrency()}
                  width={["20px", "22px", "24px"]}
                  height={["20px", "22px", "24px"]}
                  mr={2}
                />
                <Text fontSize={["sm", "md", "md"]}>
                  You&apos;ve earned{" "}
                  {currentRewardAmount
                    ? currentRewardAmount.toLocaleString()
                    : ""}{" "}
                  {currentRewardMetadata.rewardCurrency(currentRewardAmount)}
                </Text>
              </Box>
              <Text fontSize={["sm", "md", "md"]} color="fg.muted">
                {currentRewardMetadata.message ||
                  "Spin again tomorrow for another chance"}
              </Text>
            </>
          ) : !isAuthenticated || !recentRewardIsLoading ? (
            <>
              <Image
                src={chestIcon}
                alt="Chest"
                width={["36px", "40px", "44px", "48px"]}
                height={["36px", "40px", "44px", "48px"]}
                mb={4}
                mx="auto"
              />
              <Text fontSize={["xl", "xl", "2xl"]} fontWeight="bold">
                {REWARD_CATEGORY_METADATA[RewardCategory.NotYetSpun].title}
              </Text>
              <Text fontSize={["sm", "md", "md"]} color="fg.muted">
                {REWARD_CATEGORY_METADATA[RewardCategory.NotYetSpun].message}
              </Text>
            </>
          ) : (
            <Spinner />
          )}
        </Box>
      </Box>
    </Box>
  );
};
